import type { Assessment } from "../assessment-types";

/** 🪞 Self & Identity + 🌙 Daily Life — phase 1 expansion. */

export const CHILDHOOD_EMOTIONAL_NEGLECT_ASSESSMENT: Assessment = {
  slug: "childhood-emotional-neglect",
  emoji: "🧒",
  title: "Childhood Emotional Neglect Patterns",
  tagline: "How emotional needs were recognised and responded to",
  category: "self-identity",
  keywords: ["childhood", "neglect", "emotional needs", "inner child", "growing up", "family"],
  intro:
    "This reflects on whether your feelings were noticed and responded to when you were young, and how that may still shape you now. It is about experiences, not about blaming anyone.",
  questions: [
    { id: "q1", text: "Do you find it hard to name what you're feeling?" },
    { id: "q2", text: "Did you learn early that your feelings were an inconvenience?" },
    { id: "q3", text: "Do you feel uncomfortable when someone asks how you really are?" },
    { id: "q4", text: "Do you feel there is something missing that you can't quite describe?" },
    { id: "q5", text: "Did you often manage difficult feelings on your own as a child?" },
    { id: "q6", text: "Do you struggle to know what you want or need?" },
    { id: "q7", text: "Do you feel guilty for having needs at all?" },
    { id: "q8", text: "Do you find emotional conversations difficult to stay in?" },
    { id: "q9", text: "Do you sometimes feel empty rather than sad?" },
    { id: "q10", text: "Do you assume others will not be interested in how you feel?" },
    { id: "q11", text: "Do you compare your childhood to others' and conclude yours 'wasn't that bad'?" },
    { id: "q12", text: "Would it help to build a clearer relationship with your own feelings?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest you can usually recognise and express what you feel.",
      body:
        "That capacity is a resource. Keeping it in practice helps in the moments when feelings are harder to reach.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest some of your emotional needs may not have been met early on.",
      body:
        "Emotional neglect is often about what didn't happen rather than what did, which is why it can be hard to name. Learning the language of feelings is a very workable place to start.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest patterns linked with unmet emotional needs may be present.",
      body:
        "This can show up as difficulty naming feelings, discomfort with support, or a quiet sense of emptiness. None of it means you are broken.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest emotional needs may have gone largely unrecognised for you.",
      body:
        "That leaves a real mark, even when nothing dramatic happened. Working with someone appropriately qualified, alongside gentle self-reflection, is often meaningful with this pattern.",
    },
  ],
  explore: {
    understand: [
      { label: "Inner child work", to: "/learn/inner-child" },
      { label: "Complex trauma", to: "/learn/cptsd" },
    ],
    try: [
      { label: "Self-love practices", to: "/self-love" },
      { label: "Acts of self-care", to: "/acts-of-self-care" },
    ],
    reflect: [
      { label: "Inner child reflections", to: "/inner-child" },
      { label: "Letters never sent", to: "/integrate/letters-never-sent" },
    ],
  },
};

export const SLEEP_AND_TRAUMA_ASSESSMENT: Assessment = {
  slug: "sleep-and-trauma",
  emoji: "🌙",
  title: "Sleep & Trauma",
  tagline: "How safe and settled does your system feel at night?",
  category: "daily-life",
  keywords: ["sleep", "insomnia", "nightmares", "night", "rest", "bedtime", "waking"],
  intro:
    "This reflects on what nights are like for you. Sleep is often where an unsettled nervous system becomes most noticeable.",
  questions: [
    { id: "q1", text: "Do you find it hard to fall asleep even when you're tired?" },
    { id: "q2", text: "Do you wake during the night and struggle to settle again?" },
    { id: "q3", text: "Do you feel more alert or uneasy at night?" },
    { id: "q4", text: "Do you experience unsettling dreams or nightmares?" },
    { id: "q5", text: "Do you check the doors, windows or your phone before you can rest?" },
    { id: "q6", text: "Do your thoughts get louder once the day is quiet?" },
    { id: "q7", text: "Do you delay going to bed?" },
    { id: "q8", text: "Do you wake feeling as though you haven't rested?" },
    { id: "q9", text: "Do you need particular conditions — light, sound, company — to feel safe enough to sleep?" },
    { id: "q10", text: "Do you feel tense in your body when you lie down?" },
    { id: "q11", text: "Does poor sleep affect how you cope the following day?" },
    { id: "q12", text: "Would you like nights to feel calmer than they currently do?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest nights are reasonably settled for you at the moment.",
      body:
        "Sleep is one of the clearest signals of how safe your system feels. Noticing what supports yours is worth keeping hold of.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest sleep is sometimes unsettled for you.",
      body:
        "When the day goes quiet, the nervous system often has more room to react. A predictable wind-down can gently signal that it's safe to rest.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest sleep may frequently be disrupted.",
      body:
        "Difficult nights are common after difficult experiences, and they affect everything else. Small, repeatable evening practices often help more than trying harder to sleep.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest nights may be consistently hard for you.",
      body:
        "Ongoing sleep difficulty deserves proper care, and it may be worth speaking with a GP or an appropriately qualified professional. Gentle evening regulation can sit alongside that.",
    },
  ],
  explore: {
    understand: [
      { label: "Trauma and sleep", to: "/learn/trauma-sleep" },
      { label: "Hypervigilance explained", to: "/learn/hypervigilance" },
    ],
    try: [
      { label: "Sleep support", to: "/sleep" },
      { label: "Relaxation practices", to: "/relaxation-practices" },
    ],
    reflect: [
      { label: "Dreams and night reflections", to: "/dreams" },
      { label: "Daily check-in", to: "/check-in" },
    ],
  },
};
